/**
 * Release 3 EXP-1 (lane C) — the download menu: "Original" + the workspace presets (images only) + "Custom export…"
 * (members). Used as menu items in the grid context menu and as a button + menu in the asset overlay; share guests get
 * the presets the public resolve carried and go through `publicDownload`.
 */
import { useRef, useState, type ReactNode } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import type { MenuItem } from '@/components/Menu';
import { Menu } from '@/components/Menu';
import { IconButton } from '@/components/Button';
import { useDownloadPresets, useExportVersion, type ImageTransform } from '@/lib/api/queries';
import { useSessionStore } from '@/stores/session';
import { ExportDialog } from './ExportDialog';
import { describeTransform, exportFilename, type PresetLike } from './exportModel';
import { completeExport, runPublicExport } from './runExport';

export type DownloadTarget = { versionId: string; assetId: string; filename: string; isImage: boolean };

/** Menu entries for one asset: original, one per preset (images), custom (members). `onOriginal` keeps the caller's legacy path. */
export function downloadMenuItems(opts: {
  target: DownloadTarget;
  presets: readonly PresetLike[];
  onOriginal: () => void;
  onPreset: (preset: PresetLike) => void;
  onCustom?: () => void;
  originalLabel?: string;
}): MenuItem[] {
  const items: MenuItem[] = [{ label: opts.originalLabel ?? 'Download original', onSelect: opts.onOriginal }];
  if (!opts.target.isImage) return items;
  if (opts.presets.length) items.push({ kind: 'label', label: 'Export as' });
  for (const p of opts.presets) items.push({ label: p.name, title: `${describeTransform(p.transform as ImageTransform)} → ${exportFilename(opts.target.filename, p.transform as ImageTransform)}`, onSelect: () => opts.onPreset(p) });
  if (opts.onCustom) items.push({ label: 'Custom export…', onSelect: opts.onCustom });
  return items;
}

/**
 * Button + menu for the asset overlay header. Members: presets from the workspace + custom dialog (`downloadVersion`);
 * share guests: presets from the resolve (`publicDownload` with `preset_id`). Non-images render the plain original button.
 */
export function DownloadMenuButton({ target, onOriginal, share, label = 'Download' }: { target: DownloadTarget; onOriginal: () => void; share?: { token: string; presets: readonly PresetLike[] } | null; label?: string }) {
  const wsId = useSessionStore((s) => s.workspace?.id);
  const presetsQ = useDownloadPresets(share ? undefined : wsId, target.isImage);
  const exportM = useExportVersion();
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  const presets = share ? share.presets : (presetsQ.data ?? []);
  if (!target.isImage) {
    return (
      <IconButton label="Download original" onClick={onOriginal}>
        <Download size={16} />
      </IconButton>
    );
  }
  const items = downloadMenuItems({
    target,
    presets,
    onOriginal,
    onPreset: (p) => {
      const name = exportFilename(target.filename, p.transform as ImageTransform);
      if (share) void runPublicExport(share.token, target.assetId, p.id, { filename: name });
      else exportM.mutate({ versionId: target.versionId, presetId: p.id }, { onSuccess: (r) => void completeExport(r, { filename: name }) });
    },
    onCustom: share ? undefined : () => setCustom(true),
  });
  const trigger: ReactNode = (
    <IconButton ref={btn} label={label} onClick={() => setOpen(true)} aria-haspopup="menu" aria-expanded={open} data-download-menu>
      <Download size={16} />
      <ChevronDown size={10} />
    </IconButton>
  );
  return (
    <>
      {trigger}
      {open && <Menu anchor={btn.current} items={items} onClose={() => setOpen(false)} />}
      {custom && <ExportDialog versionId={target.versionId} filename={target.filename} onClose={() => setCustom(false)} />}
    </>
  );
}
